import React, { useEffect, useMemo, useState } from "react";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { useNavigate, useParams } from "react-router-dom";
import { getProductinUniqueDepartment } from "../../Redux/orders";
import FullScreenLoader from "../../Components/FullScreenLoader";
import {
  DatePicker,
  Dropdown,
  Image,
  Menu,
  message,
  Select,
  Table,
  TableColumnsType,
  Tabs,
  TabsProps,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import {
  CapitalizeFirstWord,
  DefaultNoImageFound,
  generateDeptTabsStyle,
  getDepartmentTagTheme,
  getThumbnailUrl,
  momentDateTime,
  momentDDMMYY,
} from "../../../helpers/utils";
import { decryptByText, responseDataFormate } from "../../../helpers/bycrypt";
import { svg } from "../../../Assets/svg";
import { OrderStatusEnum } from "./orderTypes";
import AddOrderProductionQtyModal from "../Manufacturing/Components/AddOrderProductionQtyModal";
import { EyeOutlined } from "@ant-design/icons";
import axiosInstance from "../../../helpers/axios";
import { GetMachineListAPI } from "../../Redux/masterData";
import CommonFilePreviewModal from "../../Components/CommonFilePreviewModal";
import { AttachedDrawings } from "../Quotations/QuotationDetails/ProductDetails";
import DescriptionWithShowMore from "../../../helpers/DescriptionWithShowMore";

const ProductionJobs = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [previewFiles, setPreviewFiles] = useState<any>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);

  const { orderDetails, getOrderProductionDepartment } = useAppSelector(
    (state: RootState) => state.orders,
  );

  const { machineList } = useAppSelector(
    (state: RootState) => state.masterData,
  );

  const [jobsList, setJobsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategorySnapShot, setSelectedCategorySnapShot] =
    useState<any>("");
  const [selectedMachine, setSelectedMachine] = useState<any>("");
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [activeKey, setActiveKey] = useState<any>("all");
  const [openAddQtyModal, setOpenAddQtyModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(500);
  const [totalCount, setTotalCount] = useState(0);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    dispatch(GetMachineListAPI({ limit: 1000 }, () => {}));
  }, [dispatch]);

  const filteredMachines = useMemo(() => {
    if (!machineList) return [];
    if (activeKey === "all") return machineList;
    return machineList.filter(
      (m: any) =>
        m?.department?._id === activeKey || m?.department === activeKey,
    );
  }, [machineList, activeKey]);

  useEffect(() => {
    const checkScreen = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  // Fetch unique departments for tabs
  useEffect(() => {
    if (id) {
      dispatch(
        getProductinUniqueDepartment({
          order_id: id,
          categories_snapshot_id: selectedCategorySnapShot,
        }),
      );
    }
  }, [id, selectedCategorySnapShot, dispatch]);

  useEffect(() => {
    setSelectedMachine("");
    setPage(1);
  }, [activeKey]);

  // Fetch job list based on filters, tabs, page & limit
  const fetchJobsList = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const params: any = {
        page,
        limit,
        tz:
          Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Calcutta"
            ? "Asia/Kolkata"
            : Intl.DateTimeFormat().resolvedOptions().timeZone ||
              "Asia/Kolkata",
      };
      if (selectedCategorySnapShot) {
        params.categories_snapshot_id = selectedCategorySnapShot;
      }
      if (activeKey !== "all") {
        params.department_id = activeKey;
      }
      if (selectedMachine) {
        params.machine_id = selectedMachine;
      }
      if (selectedDate) {
        params.today_date = dayjs(selectedDate).startOf("day").toISOString();
      }

      const response = await axiosInstance.get(
        `/orders/production-manage/jobs-data-by-order/${id}`,
        { params },
      );
      const result = responseDataFormate(response.data);
      if (result?.success) {
        const rawData = result?.data?.data || [];
        setTotalCount(result?.data?.count || 0);
        // const transformed = transformApiData(result?.data?.data);
        setJobsList(rawData);
      } else {
        message.error(
          result?.message || "Failed to fetch production manage jobs",
        );
      }
    } catch (err) {
      console.error("fetchJobsList error:", err);
      message.error("Failed to fetch production manage jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobsList();
  }, [
    id,
    activeKey,
    selectedCategorySnapShot,
    selectedMachine,
    selectedDate,
    page,
    limit,
  ]);

  const items: TabsProps["items"] = useMemo(() => {
    if (getOrderProductionDepartment?.length > 0) {
      const data = getOrderProductionDepartment.map((elem: any) => ({
        key: elem?._id,
        label: `${elem?.name} ${
          elem?.incomplete_count > 0 || elem?.complete_count > 0
            ? `(${elem?.complete_count}/${
                elem?.incomplete_count + elem?.complete_count
              })`
            : ""
        }`,
        children: "",
      }));
      const totalComplateIncomplateTask = getOrderProductionDepartment.reduce(
        (acc: any, elem: any) => {
          acc.complete_count += elem?.complete_count;
          acc.incomplete_count += elem?.incomplete_count;
          return acc;
        },
        { complete_count: 0, incomplete_count: 0 },
      );
      return [
        {
          key: "all",
          label: `All (${totalComplateIncomplateTask?.complete_count}/${
            totalComplateIncomplateTask?.incomplete_count +
            totalComplateIncomplateTask?.complete_count
          })`,
        },
        ...data,
      ];
    }
    return [];
  }, [getOrderProductionDepartment]);

  const deptTabsCss = useMemo(() => {
    return generateDeptTabsStyle(
      "productionJobsDeptTabs",
      getOrderProductionDepartment,
      activeKey,
    );
  }, [getOrderProductionDepartment, activeKey]);

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        {/* {data?.items?.splits?.status !== "completed" ? (
          <Menu.Item
            key="1"
            onClick={() => {
              setSelectedRecord(data);
              setOpenAddQtyModal(true);
            }}
          >
            <span className="cursor-pointer" style={{ color: "#231F20" }}>
              Add QTY
            </span>
          </Menu.Item>
        ) : null} */}

        {[
          OrderStatusEnum.ORDER_CREATED,
          OrderStatusEnum.PRODUCTION_STARTED,
          OrderStatusEnum.PRODUCTION_PLANNING,
        ]?.includes(orderDetails?.order_status) ? (
          <Menu.Item
            key="2"
            onClick={() => {
              navigate(
                `/manufacturing/${data._id}/${data?.items?.splits?._id}`,
                { state: { isNoFilter: true } },
              );
            }}
          >
            <span className="cursor-pointer" style={{ color: "#231F20" }}>
              View
            </span>
          </Menu.Item>
        ) : null}
      </Menu>
    );
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Product",
      onCell: (record: any) => ({ rowSpan: record.rowSpan }),
      render: (_: any, record: any) => {
        console.log(record, "recordrecordrecordrecord");
        return (
          <>
            <div className="d-flex gap-2">
              <Image
                src={
                  record?.product?.image &&
                  record?.product?.image !== DefaultNoImageFound
                    ? getThumbnailUrl(record?.product?.image)
                    : DefaultNoImageFound
                }
                alt={record?.product?.name}
                className="rounded-2 object-fit-cover border w-100 h-100"
                rootClassName="img-45"
                fallback={DefaultNoImageFound}
                preview={{
                  mask: <EyeOutlined />,
                }}
              />
              <div className="d-flex flex-column">
                <p
                  className="p-0 m-0"
                  style={{
                    color:
                      record?.items?.item_type == "bom_part"
                        ? "#EA7E58"
                        : record?.items?.item_type == "component"
                        ? "#A756AC"
                        : "#58BF6E",
                  }}
                >
                  {record?.items?.item_type == "bom_part"
                    ? "Part"
                    : record?.items?.item_type == "component"
                    ? "Component"
                    : "Variant"}
                </p>
                <h6 className="fw-semibold mb-0 text-dark fs-6 mb-1">
                  {["bom_part", "component"]?.includes(record?.items?.item_type)
                    ? record?.items?.name
                    : record?.items?.variant_name ||
                      record?.items?.splits?.variant_id?.variant_name ||
                      record?.items?.product?.name ||
                      "-"}
                </h6>
                <h6
                  className="fw-semibold mb-0 text-dark"
                  style={{ fontSize: "12px" }}
                >
                  {record?.items?.product?.labelInQuotation || "Qty"} -{" "}
                  {record?.items?.quantity}
                </h6>
              </div>
            </div>
            <small className="text-muted fw-normal fs-8 d-inline-block mt-1 device-w-fix">
              {record?.items?.product?.description ? (
                <DescriptionWithShowMore
                  description={record?.items?.product?.description}
                />
              ) : (
                ""
              )}
            </small>
            <AttachedDrawings
              drawings={
                record?.items?.drawings ||
                record?.items?.splits?.variant_id?.drawings ||
                []
              }
              setPreviewModal={setPreviewModalOpen}
              setPreviewFiles={setPreviewFiles}
              setPreviewIndex={setPreviewIndex}
              layout="table"
            />
          </>
        );
      },
      width: "16%",
    },
    // {
    //   title: "Drawings",
    //   width: "10%",
    //   onCell: (record: any) => ({ rowSpan: record.rowSpan }),
    //   render: (_: any, record: any) => {
    //     const drawings =
    //       record?.items?.drawings ||
    //       record?.items?.splits?.variant_id?.drawings ||
    //       [];
    //     return drawings?.length > 0 ? (
    //       <AttachedDrawings
    //         drawings={drawings}
    //         setPreviewModal={setPreviewModalOpen}
    //         setPreviewFiles={setPreviewFiles}
    //         setPreviewIndex={setPreviewIndex}
    //         layout="table"
    //       />
    //     ) : (
    //       "-"
    //     );
    //   },
    // },
    {
      title: "Qty",
      dataIndex: "qty",
      width: "5%",
      render: (_: any, record: any) => (
        <div>
          <span>{record?.items?.splits?.qty || 0} Qty</span>
          <div className="fs-8 text-secondary mt-1"></div>
        </div>
      ),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      render: (unit: any) => <span>{unit || "-"}</span>,
      width: "4%",
    },
    {
      title: "Department",
      dataIndex: "department_id",
      width: "9%",
      render: (department_id: any, record: any) => {
        const deptObj = record?.items?.splits?.department_id;
        const deptName = deptObj?.name || (typeof deptObj === "string" ? deptObj : "-");
        const deptIdStr = deptObj?._id ? String(deptObj._id) : (typeof deptObj === "string" ? deptObj : "");
        const allDrawings =
          record?.items?.drawings ||
          record?.items?.splits?.variant_id?.drawings ||
          [];

        const deptDrawings = deptIdStr
          ? allDrawings.filter((d: any) => {
              if (!d) return false;
              if (Array.isArray(d.departments) && d.departments.length > 0) {
                return d.departments.some((dept: any) => {
                  const id =
                    typeof dept === "object"
                      ? String(dept?._id || dept?.id || "")
                      : String(dept);
                  return id === deptIdStr;
                });
              }
              return false;
            })
          : [];

        return (
          <div className="d-flex flex-column gap-1">
            <span>{deptName}</span>
            {deptDrawings.length > 0 && (
              <div className="mt-1">
                <AttachedDrawings
                  drawings={deptDrawings}
                  setPreviewModal={setPreviewModalOpen}
                  setPreviewFiles={setPreviewFiles}
                  setPreviewIndex={setPreviewIndex}
                  layout="department"
                />
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Machine",
      dataIndex: "machine_id",
      width: "9%",
      render: (machine_id: any, record: any) => (
        <span>
          {record?.items?.splits?.machine_id?.name
            ? record?.items?.splits?.machine_id?.name
            : "-"}
        </span>
      ),
    },
    {
      title: "Employee",
      dataIndex: "employee_details",
      width: "9%",
      render: (employee_details: any, record: any) => (
        <span>
          {record?.items?.splits?.employee_details?.length > 0
            ? record?.items?.splits?.employee_details
                ?.map(
                  (elem: any) =>
                    CapitalizeFirstWord(decryptByText(elem?.first_name)) +
                    " " +
                    CapitalizeFirstWord(decryptByText(elem?.last_name)),
                )
                ?.join(", ")
            : "-"}
        </span>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "start_at",
      width: "9%",
      render: (start_at: any, record: any) => {
        return record?.items?.splits?.start_at
          ? momentDDMMYY(record?.items?.splits?.start_at)
          : "-";
      },
    },
    {
      title: "End Date",
      dataIndex: "end_at",
      width: "9%",
      render: (end_at: any, record: any) => {
        return record?.items?.splits?.end_at
          ? momentDDMMYY(record?.items?.splits?.end_at)
          : "-";
      },
    },
    {
      title: "Manufactured Qty",
      dataIndex: "manufactured_qty",
      width: "6%",
      render: (manufactured_qty: any, record: any) => (
        <span>
          {record?.items?.splits?.manufactured_qty
            ? record?.items?.splits?.manufactured_qty
            : 0}
        </span>
      ),
    },
    {
      title: "Pending Qty",
      dataIndex: "pending_qty",
      width: "6%",
      render: (pending_qty: any, record: any) => (
        <span>
          {record?.items?.splits?.pending_qty
            ? record?.items?.splits?.pending_qty
            : 0}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: "8%",
      render: (newStatus: any, record: any) => {
        const status = record?.items?.splits?.status;
        const statusClassMap = {
          started: "designPendingStatus",
          completed: "approvedStatus",
          scheduled: "draftStatus",
        };

        const displayLabel =
          status === "init"
            ? ""
            : status === "started"
            ? "Started"
            : status === "completed"
            ? "Completed"
            : "Scheduled";
        return displayLabel === "" ? (
          <>-</>
        ) : (
          <span
            className={`d-inline-flex align-items-center billStatus ${
              statusClassMap[status as keyof typeof statusClassMap] || ""
            }`}
          >
            {svg.dotIcon}
            {displayLabel}
          </span>
        );
      },
    },
    ...([
      OrderStatusEnum.PRODUCTION_STARTED,
      OrderStatusEnum.PRODUCTION_PLANNING,
    ]?.includes(orderDetails?.order_status)
      ? [
          {
            title: "Action",
            key: "action",
            width: "8%",
            render: (_: any, record: any) => {
              return (
                <div
                  className="d-flex justify-content-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Dropdown
                    overlay={() => MoreOptionsMenu(record)}
                    trigger={["click"]}
                    placement="bottomRight"
                    className="cursor-pointer"
                    overlayClassName="moreOptionDropdown"
                  >
                    <span className="svg-28 svg-outline-primary">
                      {svg?.tableMoreOption}
                    </span>
                  </Dropdown>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <h4 className="fw-bold mb-0 fs-4 d-flex align-items-center gap-2">
          Jobs
        </h4>

        <div className="d-flex flex-wrap align-items-center gap-2 ms-sm-auto mob-w-100">
          <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
            <Select
              value={selectedCategorySnapShot}
              popupClassName="full-text-select-dropdown"
              className="form-select form-select-lg"
              onChange={(value) => {
                setSelectedCategorySnapShot(value);
                setPage(1);
              }}
            >
              <Select.Option value={""}>All Categories</Select.Option>
              {orderDetails?.categories_snapshot?.map((elem: any) => {
                return (
                  <Select.Option value={elem?._id} key={elem?._id}>
                    {elem?.name}
                  </Select.Option>
                );
              })}
            </Select>
          </div>

          <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
            <Select
              value={selectedMachine}
              popupClassName="full-text-select-dropdown"
              className="form-select form-select-lg"
              onChange={(value) => {
                setSelectedMachine(value);
                setPage(1);
              }}
            >
              <Select.Option value={""}>All Machines</Select.Option>
              {filteredMachines?.map((elem: any) => {
                return (
                  <Select.Option value={elem?._id} key={elem?._id}>
                    {elem?.name}
                  </Select.Option>
                );
              })}
            </Select>
          </div>

          <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
            <DatePicker
              value={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                setPage(1);
              }}
              placeholder="Select Date"
              format="DD-MM-YYYY"
              suffixIcon={svg?.calenderIcon}
              className="w-100 form-control-lg"
              size="large"
              allowClear={true}
            />
          </div>
        </div>
      </div>

      {loading && jobsList.length === 0 ? (
        <div
          className="w-100 d-flex align-items-center justify-content-center"
          style={{ height: "calc(100vh - 17rem)" }}
        >
          <FullScreenLoader />
        </div>
      ) : (
        <div>
          <div className="customHorizontalTabs productionJobsDeptTabs bg-white overflow-hidden border-0 rounded-3 d-inline-block mw-100 mb-2">
            {deptTabsCss && <style>{deptTabsCss}</style>}
            <Tabs
              activeKey={activeKey}
              onChange={(key: string) => {
                setActiveKey(key);
                setPage(1);
              }}
              items={items?.map(({ label, key }) => ({
                label,
                key,
              }))}
            />
          </div>
          <div
            className={`card page-table-card variantsTableCard border-0 shadow rounded-4 flex-fill overflow-hidden`}
          >
            <div className="card-body overflow-y-auto h-100 p-0">
              <Table
                columns={columns}
                dataSource={jobsList}
                scroll={isMobile ? { x: "max-content" } : undefined}
                loading={loading}
                pagination={false}
                bordered
                className="variant-edit-table productionListTable bg-white"
                size="middle"
              />
            </div>
          </div>
        </div>
      )}

      {openAddQtyModal && selectedRecord && (
        <AddOrderProductionQtyModal
          visible={openAddQtyModal}
          setIsModalVisible={(val: boolean) => {
            setOpenAddQtyModal(val);
            if (!val) {
              setSelectedRecord(null);
            }
          }}
          details={{
            order: { _id: id },
            _id: selectedRecord._id,
            items: {
              _id: selectedRecord.items?._id,
              item_type: selectedRecord.items?.item_type,
              name: selectedRecord.items?.name,
              quantity: selectedRecord.items?.quantity,
              product: {
                labelInQuotation:
                  selectedRecord.items?.product?.labelInQuotation || "Qty",
              },
              splits: {
                _id: selectedRecord.items?.splits?._id,
                pending_qty: selectedRecord.items?.splits?.pending_qty,
                manufactured_qty:
                  selectedRecord.items?.splits?.manufactured_qty,
                total_manufactured_qty:
                  selectedRecord.items?.splits?.total_manufactured_qty,
                department_id:
                  typeof selectedRecord.items?.splits?.department_id ===
                  "object"
                    ? selectedRecord.items?.splits?.department_id
                    : { _id: selectedRecord.items?.splits?.department_id },
                machine_id:
                  typeof selectedRecord.items?.splits?.machine_id === "object"
                    ? selectedRecord.items?.splits?.machine_id
                    : { _id: selectedRecord.items?.splits?.machine_id },
                quality_checklists:
                  selectedRecord.items?.splits?.quality_checklists || [],
              },
            },
          }}
          selectedLog={null}
          isDetailsPage={false}
          users={selectedRecord?.items?.splits?.employee_details?.map(
            (elem: any) => {
              const name = `${CapitalizeFirstWord(
                decryptByText(elem?.first_name),
              )} ${CapitalizeFirstWord(decryptByText(elem?.last_name))}`;
              return {
                label: name,
                value: elem?._id,
              };
            },
          )}
          setRerenderLog={() => {}}
          setCurrentPage={() => {}}
          onSuccess={() => {
            fetchJobsList();
            if (id) {
              dispatch(
                getProductinUniqueDepartment({
                  order_id: id,
                  categories_snapshot_id: selectedCategorySnapShot,
                }),
              );
            }
          }}
        />
      )}
      {previewModalOpen && (
        <CommonFilePreviewModal
          visible={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewFiles([]);
            setPreviewIndex(0);
          }}
          files={previewFiles}
          activeIndex={previewIndex}
          onActiveIndexChange={setPreviewIndex}
        />
      )}
    </div>
  );
};

export default ProductionJobs;
